/**
 * Logger sicuro che nasconde informazioni sensibili
 * Previene l'esposizione di API keys, tokens e dati personali nei log
 */

const SENSITIVE_PATTERNS = [
  /Bearer\s+[\w\-\.]+/gi,
  /api[_-]?key[\s:=]+[\w\-]+/gi,
  /token[\s:=]+[\w\-\.]+/gi,
  /password[\s:=]+[\w\-\.]+/gi,
  /teable_[\w\-\.]+/gi,
];

const SENSITIVE_FIELDS = [
  'apiKey',
  'api_key',
  'authorization',
  'bearer',
  'token',
  'password',
  'secret',
];

/**
 * Rimuove informazioni sensibili da una stringa
 */
const sanitizeString = (str: string): string => {
  let sanitized = str;
  SENSITIVE_PATTERNS.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '[REDACTED]');
  });
  return sanitized;
};

/**
 * Rimuove informazioni sensibili da un oggetto
 */
const sanitizeObject = (obj: any): any => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }

  const sanitized: any = {};
  for (const [key, value] of Object.entries(obj)) {
    const lowerKey = key.toLowerCase();
    
    if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeString(value);
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Verifica se siamo in ambiente di produzione
 */
const isProduction = (): boolean => {
  return import.meta.env.PROD || import.meta.env.MODE === 'production';
};

/**
 * Logger sicuro con rimozione automatica di dati sensibili
 * In produzione, i log sono completamente disabilitati
 */
export const secureLog = {
  log: (...args: any[]) => {
    if (isProduction()) return;
    const sanitized = args.map(arg => 
      typeof arg === 'string' ? sanitizeString(arg) : sanitizeObject(arg)
    );
    console.log(...sanitized);
  },
  
  error: (...args: any[]) => {
    if (isProduction()) return;
    const sanitized = args.map(arg => 
      typeof arg === 'string' ? sanitizeString(arg) : sanitizeObject(arg)
    );
    console.error(...sanitized);
  },
  
  warn: (...args: any[]) => {
    if (isProduction()) return;
    const sanitized = args.map(arg => 
      typeof arg === 'string' ? sanitizeString(arg) : sanitizeObject(arg)
    );
    console.warn(...sanitized);
  },
  
  info: (...args: any[]) => {
    if (isProduction()) return;
    const sanitized = args.map(arg => 
      typeof arg === 'string' ? sanitizeString(arg) : sanitizeObject(arg)
    );
    console.info(...sanitized);
  },
};

/**
 * Sanitizza un messaggio di errore per mostrarlo all'utente
 * Rimuove stack traces e informazioni tecniche sensibili
 */
export const sanitizeErrorMessage = (error: any): string => {
  if (typeof error === 'string') {
    return sanitizeString(error);
  }
  
  if (error instanceof Error) {
    // Non mostrare stack trace all'utente
    const message = error.message || 'Si è verificato un errore';
    return sanitizeString(message);
  }
  
  return 'Si è verificato un errore imprevisto';
};
